import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  updatePassword
} from "firebase/auth";

import { auth, firebaseConfig } from "../firebase";
import { useErrorBoundary } from "react-error-boundary";

const AuthContext = createContext();

export function AuthProvider({ children, userData }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState('')
  const [claims, setClaims] = useState('')
  const [accountID, setAccountID] = useState([])
  const [customerNames, setCustomerNames] = useState({})



  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
  }

  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function change(newPassword) {
    return updatePassword(auth.currentUser, newPassword)
  }

  function changeAccountID(newAccountID) {
    setAccountID(newAccountID)
  }

  function logout() {
    return signOut(auth);
  }

  async function refreshAuthUser() {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return null;
    }

    await currentUser.reload();
    const refreshedUser = auth.currentUser;
    setUser(refreshedUser);

    const latestToken = await refreshedUser.getIdToken(true);
    setToken(latestToken);

    const idTokenResult = await refreshedUser.getIdTokenResult(true);
    setClaims(idTokenResult.claims);

    return {
      user: refreshedUser,
      claims: idTokenResult.claims,
      token: latestToken,
    };
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (_user) => {
      // console.log("Auth", _user);

      setUser(_user);
      let sessionTimeout = null;

      if (_user) {
        console.log("Auth - onAuthStateChanged", _user);

        _user.getIdToken(true) // 1
          .then(latestToken => {
            setToken(latestToken)
          })
          .catch(err => console.log(err))

        _user.getIdTokenResult(true) // 1
          .then((idTokenResult) => {
            setClaims(idTokenResult.claims);
            
            // Initialize accountID with saved selection or first account
            // Trim each ID to remove any spaces
            const allAccountIds = idTokenResult.claims.aid.split(",").map(id => id.trim());
            let initialAccountIds = allAccountIds;
            
            // Try to load saved selection from localStorage
            try {
              const savedSelection = localStorage.getItem('selectedAccounts');
              if (savedSelection) {
                const savedAccounts = JSON.parse(savedSelection);
                const validSavedAccounts = savedAccounts.filter(account => 
                  allAccountIds.includes(account)
                );
                if (validSavedAccounts.length > 0) {
                  console.log("useAuth - Restored account selection from memory:", validSavedAccounts);
                  initialAccountIds = validSavedAccounts;
                } else {
                  // Use only first account if multiple exist
                  initialAccountIds = allAccountIds.length > 1 ? [allAccountIds[0]] : allAccountIds;
                }
              } else {
                // Use only first account if multiple exist
                initialAccountIds = allAccountIds.length > 1 ? [allAccountIds[0]] : allAccountIds;
              }
            } catch (error) {
              console.warn("useAuth - Error loading saved account selection:", error);
              initialAccountIds = allAccountIds.length > 1 ? [allAccountIds[0]] : allAccountIds;
            }
            
            console.log("useAuth - Setting initial accountID:", initialAccountIds);
            setAccountID(initialAccountIds);

            // Fetch customer names immediately after login
            // Use /app/customers endpoint instead of /app/orders to get names for ALL customers
            // (including those without orders)
            const fetchCustomerNames = async () => {
              const names = {};
              
              try {
                // Fetch all customer names in one request
                const kdnrsParam = allAccountIds.join(',');
                const response = await apiFetch(`/app/customers?kdnrs=${kdnrsParam}`);
                
                if (response.ok) {
                  const data = await response.json();
                  // data is an array of customer objects with kundennummer and Name
                  data.forEach(customer => {
                    if (customer.Name) {
                      names[customer.kundennummer] = customer.Name.trim();
                    } else {
                      names[customer.kundennummer] = '';
                    }
                  });
                  
                  // Set empty names for customers not found in database
                  allAccountIds.forEach(kdnr => {
                    if (!(kdnr in names)) {
                      names[kdnr] = '';
                    }
                  });
                } else {
                  // If request fails, set all names to empty
                  allAccountIds.forEach(kdnr => {
                    names[kdnr] = '';
                  });
                }
              } catch (err) {
                console.error('useAuth - Error fetching customer names:', err);
                // Set all names to empty on error
                allAccountIds.forEach(kdnr => {
                  names[kdnr] = '';
                });
              }
              
              console.log("useAuth - Customer names loaded:", names);
              setCustomerNames(names);
            };
            
            fetchCustomerNames();

            // firebase logout after 1 hour inactivity

            const sessionDuration = 1000 * 60 * 60; // 1 hour in milliseconds
            let sessionTimer = null;

            const resetSessionTimer = () => {
              clearTimeout(sessionTimer);
              sessionTimer = setTimeout(() => {
                auth.signOut().then(() => {
                  console.log("User signed out due to inactivity");
                }).catch((error) => {
                  console.log(error);
                });
              }, sessionDuration);
            }

            resetSessionTimer();

            document.addEventListener("mousemove", resetSessionTimer);
            document.addEventListener("keypress", resetSessionTimer);
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        sessionTimeout && clearTimeout(sessionTimeout);
        sessionTimeout = null;
      }

    });

    return () => { unsubscribe(); };
    //}, [auth]);
    // chceck if userData is not empty
  }, [auth]);

  return (
    <AuthContext.Provider
      value={{ user, token, claims, accountID, customerNames, login, signup, logout, change, changeAccountID, refreshAuthUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(AuthContext);
};



export function apiFetch(path, options = {}) {
  var url = "https://europe-west1-" + firebaseConfig.projectId + ".cloudfunctions.net" + path;

  if (firebaseConfig.projectId === "litport-heine-c2471") {
    url = "https://europe-west3-" + firebaseConfig.projectId + ".cloudfunctions.net" + path;
  }

  if (firebaseConfig.projectId === "litport-schoeningh") {
    url = "https://europe-west1-" + firebaseConfig.projectId + ".cloudfunctions.net" + path;
  }

  console.log("apiFetch - Full URL:", url);
  console.log("apiFetch - Project ID:", firebaseConfig.projectId);

  const headers = options.headers || {};
  
  // Get the current user's ID token
  const currentUser = auth.currentUser;
  
  if (currentUser) {
    return currentUser.getIdToken()
      .then(idToken => {
        headers['Authorization'] = "Bearer " + idToken;
        // Only set Content-Type if not already specified (allow XML, etc.)
        if (!headers['Content-Type']) {
          headers['Content-Type'] = "application/json";
        }
        console.log({ ...options, headers });
        return fetch(url, { ...options, headers });
      });
  } else {
    // No user logged in, make request without auth header
    // Only set Content-Type if not already specified (allow XML, etc.)
    if (!headers['Content-Type']) {
      headers['Content-Type'] = "application/json";
    }
    console.log({ ...options, headers });
    return fetch(url, { ...options, headers });
  }
}



